import React, { useState } from "react";
import { DollarSign } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCurrencyFormat } from "@/hooks/useCurrencyFormat";

interface PriceWithUsdBadgeProps {
  /** The formatted price in the selected currency */
  formattedPrice: string;
  /** The original price in USD */
  usdPrice: number;
  /** Optional className for the container */
  className?: string;
  /** Size variant */
  size?: "sm" | "md" | "lg";
  /** Text color class */
  textColorClass?: string;
  /** Show the suffix (like "/hour") */
  suffix?: string;
  /** Suffix color class */
  suffixColorClass?: string;
}

/**
 * Displays a price with a small USD estimation badge.
 * Clicking the badge toggles between showing the local price and the USD price.
 * Only shows the badge when the selected currency is NOT USD.
 */
export const PriceWithUsdBadge: React.FC<PriceWithUsdBadgeProps> = ({
  formattedPrice,
  usdPrice,
  className,
  size = "md",
  textColorClass = "text-white",
  suffix,
  suffixColorClass = "text-blue-100/80",
}) => {
  const [showUsd, setShowUsd] = useState(false);
  const { selectedCurrency } = useCurrencyFormat();

  const sizeClasses = {
    sm: {
      price: "text-sm",
      badge: "text-[8px] px-1 py-0.5",
      icon: "h-2 w-2",
      suffix: "text-[9px]",
    },
    md: {
      price: "text-base",
      badge: "text-[9px] px-1.5 py-0.5",
      icon: "h-2.5 w-2.5",
      suffix: "text-[10px]",
    },
    lg: {
      price: "text-xl",
      badge: "text-[10px] px-1.5 py-1",
      icon: "h-3 w-3",
      suffix: "text-xs",
    },
  };

  const classes = sizeClasses[size];

  // Format USD price with proper rounding
  const formatUsdPrice = (price: number) => {
    if (price < 1) {
      return `$${price.toFixed(2)}`;
    }
    return `$${Math.round(price)}`;
  };

  const usdFormatted = formatUsdPrice(usdPrice);
  
  // Only show USD badge when not already in USD
  const showBadge = selectedCurrency.code !== "USD";

  return (
    <div className={cn("flex items-center gap-1.5 flex-wrap", className)}>
      {/* Main price display */}
      <span className={cn("font-bold", classes.price, textColorClass)}>
        {showUsd ? usdFormatted : formattedPrice}
      </span>

      {/* Suffix */}
      {suffix && (
        <span className={cn(classes.suffix, suffixColorClass)}>{suffix}</span>
      )}

      {/* USD Badge - clickable to toggle, only shows when not in USD */}
      {showBadge && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowUsd(!showUsd);
          }}
          className={cn(
            "flex items-center gap-0.5 bg-emerald-600 hover:bg-emerald-500 rounded-full transition-all duration-200",
            "border border-emerald-400 cursor-pointer shadow-sm",
            classes.badge
          )}
          title={showUsd ? `Click to show ${selectedCurrency.code} price` : `≈ ${usdFormatted} USD - Click to view`}
        >
          <DollarSign className={cn(classes.icon, "text-white")} />
          <span className="text-white font-semibold">
            {showUsd ? selectedCurrency.code : `≈${Math.round(usdPrice)}`}
          </span>
        </button>
      )}
    </div>
  );
};

export default PriceWithUsdBadge;
